import { Still } from '../scenes';
import { VideoStill } from '../VideoStill';
import { CDN } from '../../lib/cdn';
import { SignUpButton, TalkToFoundersButton } from '../CTAButtons';
import { SegmentSeeker } from '../SegmentSeeker';
import { useInViewAutoAdvance } from '../../hooks/useInViewAutoAdvance';
import { FeatureIcon } from './FeatureIcon';
import {
  ECON_AFTER,
  ECON_BEFORE,
  ENT_PIPE_STAGES,
  ENTERPRISE_COMPARE_ROWS,
  ENTERPRISE_FAQ,
  ENTERPRISE_FEATURES,
  ENTERPRISE_USE_CASES,
  INTEGRATION_LOGOS,
  SECURITY_DIFFS,
  SPEC_ENTERPRISE,
  SPEC_WORKFLOW,
  type EnterpriseCompareCell,
} from '../../content/forEnterprisePage';
import { HeroLabel, PageFaqSection } from '../ds';
import { EditorialHighlights } from '../EditorialHighlights';

function EnterpriseVsCell({ cell }: { cell: EnterpriseCompareCell }) {
  return <span className={cell.tone}>{cell.label}</span>;
}

function EntPipeline() {
  const { sectionRef, active, select, inView, interval, tick } =
    useInViewAutoAdvance(ENT_PIPE_STAGES.length, { interval: 5000 });

  return (
    <section className="ent-pipe band-light" id="pipeline" ref={sectionRef}>
      <div className="container">
        <div className="sec-head reveal">
          <span className="label-mono">- The enterprise workflow</span>
          <h2>
            Brief In. <em>Approved Cut Out.</em>
          </h2>
          <p className="sub">
            Five stages, from stakeholder brief to final export - with brand guardrails, approval hooks, and an
            audit trail throughout.
          </p>
        </div>

        <div className="ent-pipe-steps reveal" role="tablist" aria-label="Pipeline stages">
          {ENT_PIPE_STAGES.map((s) => (
            <button
              key={s.idx}
              type="button"
              className={`ent-step${active === s.idx ? ' active' : ''}`}
              data-idx={s.idx}
              role="tab"
              aria-selected={active === s.idx}
              aria-controls={`ent-panel-${s.idx}`}
              onClick={() => select(s.idx)}
            >
              <div className="ent-step-num">{s.num}</div>
              <div className="ent-step-label">{s.label}</div>
              <div className="ent-step-title">{s.title}</div>
            </button>
          ))}
        </div>

        {ENT_PIPE_STAGES.map((s) => (
          <div
            key={s.idx}
            className={`ent-pipe-panel-wrap${active === s.idx ? ' active' : ''}`}
            id={`ent-panel-${s.idx}`}
            role="tabpanel"
            hidden={active !== s.idx}
          >
            <div className="ent-pipe-panel reveal">
              <div className="ent-pipe-still">
                <div className="ent-pipe-still-inner">
                  {s.video && active === s.idx ? (
                    <VideoStill src={s.video} aria-label={s.panelTitle} />
                  ) : (
                    <Still kind={s.kind} sid={`ent-pipe-${s.idx}`} />
                  )}
                </div>
                <div className="ent-pipe-overlay" />
              </div>
              <div className="ent-pipe-info">
                <span className="ent-pipe-num">{s.panelNum}</span>
                <h3>{s.panelTitle}</h3>
                <p>{s.body}</p>
                <div className="ent-pipe-tags">
                  {s.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        <SegmentSeeker active={active} interval={interval} tick={tick} inView={inView} />
      </div>
    </section>
  );
}

export function ForEnterpriseSections() {
  return (
    <div className="solutions-page">
      <header className="fm-hero band-dark">
        <div className="container hero-grid">
          <div className="reveal">
            <HeroLabel>For enterprise content teams</HeroLabel>
            <h1 className="hero-headline">
              AI video infrastructure
              <br />
              for Enterprises, <em>at scale.</em>
            </h1>
            <p className="hero-sub">
              Script-to-film generation in your pipeline, via a text-to-video API. Private models, VPC deployment,
              SSO, and dedicated support — for teams that ship every day.
            </p>
            <div className="hero-ctas">
              <TalkToFoundersButton>Talk to Sales</TalkToFoundersButton>
              <a href="#pipeline" className="btn btn-ghost">
                See the workflow
              </a>
            </div>
            <div className="hero-note">
              SOC 2 in progress · SSO / SAML 2.0 · Training data opt-out · Volume pricing
            </div>
          </div>
          <div
            className="hero-still reveal d2"
            role="img"
            aria-label="Enterprise AI film infrastructure - Induce API integrated into a studio production pipeline"
          >
            <VideoStill
              src={CDN.videos.enterpriseHeroPortrait}
              aria-label="Enterprise AI film infrastructure - Induce API integrated into a studio production pipeline"
            />
          </div>
        </div>

        {/*
        <div
          className="container ent-dashboard-wrap reveal d2"
          role="img"
          aria-label="Enterprise AI film infrastructure - Induce API integrated into a studio production pipeline"
        >
          ... ent-dashboard-wrap commented out ...
        </div>
        */}

        <div className="integration-strip">
          <div className="container">
            <span className="int-label">Works with your existing stack</span>
            <div className="int-logos">
              {INTEGRATION_LOGOS.map((logo) => (
                <span key={logo} className="int-logo">
                  {logo}
                </span>
              ))}
            </div>
          </div>
        </div>
      </header>

      <section className="answer band-light" aria-labelledby="answer-h">
        <div className="container">
          <div className="tag">- What is Induce for enterprise</div>
          <div className="answer-body">
            <h2 id="answer-h" className="a-lead">
              <b>Infrastructure, Not Shadow IT.</b>
            </h2>
            <p className="a-body">
              Induce is AI video infrastructure - not a creative toy. It replaces the usual wall of disconnected tools,
              broken brand continuity, and manual approvals with one governed pipeline: script breakdown, shot
              generation, brand guardrails, export. Every generation logged, every workspace isolated, every output
              commercially cleared.
            </p>
            <div className="a-stats reveal">
              <div className="a-stat">
                <b>10×</b>
                <span>Faster than traditional production</span>
              </div>
              <div className="a-stat">
                <b>4</b>
                <span>Engines per project - Kling, Seedance, Veo, Nano Banana</span>
              </div>
              <div className="a-stat">
                <b>∞</b>
                <span>Departmental workspaces</span>
              </div>
              <div className="a-stat">
                <b>Every tier</b>
                <span>Full commercial rights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="econ band-dark" id="economics" aria-label="Production cost comparison">
        <div className="container">
          <div className="sec-head reveal" style={{ gridColumn: '1 / -1' }}>
            <span className="label-mono">- The production math</span>
            <h2>
              Five Figures a Minute. Or <em>Three.</em>
            </h2>
          </div>
          <div className="econ-col before reveal">
            <div className="etag">{ECON_BEFORE.etag}</div>
            <h3>
              {ECON_BEFORE.title} <em>{ECON_BEFORE.titleEm}</em>
            </h3>
            <div className="econ-rows">
              {ECON_BEFORE.rows.map((row) => (
                <div key={row.label} className="econ-row">
                  <span className="item">{row.label}</span>
                  <span className="val">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="econ-div" aria-hidden="true" />
          <div className="econ-col after reveal">
            <div className="etag">{ECON_AFTER.etag}</div>
            <h3>
              {ECON_AFTER.title} <em>{ECON_AFTER.titleEm}</em>
            </h3>
            <div className="econ-rows">
              {ECON_AFTER.rows.map((row) => (
                <div key={row.label} className="econ-row">
                  <span className="item">{row.label}</span>
                  <span className="val">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <EntPipeline />

      <EditorialHighlights
        label="Enterprise workflows"
        items={[
          { label: 'Integrations', title: 'Text-to-Video API for Enterprise Pipelines', body: 'Call the Induce API directly from your CMS, DAM, or internal tools — script in, verified cut out, with full audit trails.' },
          { label: 'Learning & development', title: 'AI Training Video Generation at Scale', body: 'Turn training scripts into consistent, on-brand video across every department — without booking a studio.' },
        ]}
      />

      <section className="feat-section band-dark">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Built for enterprise</span>
            <h2>
              Security, governance, and scale. <em>Out of the box.</em>
            </h2>
            <p className="sub">
              The controls IT, legal, and compliance need - so creative doesn&apos;t wait on procurement.
            </p>
          </div>
          <div className="feat-grid reveal">
            {ENTERPRISE_FEATURES.map((f) => (
              <div key={f.title} className="feat-card">
                <div className="feat-icon">
                  <FeatureIcon name={f.icon} />
                </div>
                <div className="feat-kicker">{f.kicker}</div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
                <div className="feat-tags">
                  {f.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band-light">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Enterprise use cases</span>
            <h2>
              Every team. <em>One pipeline.</em>
            </h2>
            <p className="sub">
              From brand to L&amp;D to exec comms - Induce handles the production layer so your teams own the strategy.
            </p>
          </div>
          <div className="uc-grid reveal">
            {ENTERPRISE_USE_CASES.map((uc) => (
              <div key={uc.title} className="uc-card">
                <div className="art">
                  {'video' in uc && uc.video ? (
                    <VideoStill src={uc.video} aria-label={uc.title} />
                  ) : (
                    <Still kind={uc.kind} sid={`ent-uc-${uc.kind}`} />
                  )}
                </div>
                <div className="body">
                  <div className="kicker">{uc.kicker}</div>
                  <h3>{uc.title}</h3>
                  <p>{uc.body}</p>
                  <div className="tail">{uc.tail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band-dark" id="compare">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Platform comparison</span>
            <h2>
              One pipeline vs. <em>the alternatives.</em>
            </h2>
            <p className="sub">
              How Induce stacks up against Synthesia Enterprise (avatar-led), Runway Enterprise (clip tooling), and the
              incumbent - in-house team plus agency retainer.
            </p>
          </div>
          <div className="vs-dt-wrap reveal">
            <table className="vs-dt">
              <caption className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Comparison of Induce versus Synthesia Enterprise, Runway Enterprise, and agency plus in-house team
              </caption>
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '19%' }} />
                <col style={{ width: '19%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="th-cap" scope="col">
                    Capability
                  </th>
                  <th className="th-induce ci" scope="col">
                    <span className="tool-name">Induce</span>
                    <span className="tool-sub">Pipeline-first</span>
                  </th>
                  <th className="th-comp" scope="col">
                    <span className="tool-name">Synthesia Ent.</span>
                    <span className="tool-sub">Avatar video</span>
                  </th>
                  <th className="th-comp" scope="col">
                    <span className="tool-name">Runway Ent.</span>
                    <span className="tool-sub">Clip / VFX suite</span>
                  </th>
                  <th className="th-comp" scope="col">
                    <span className="tool-name">Agency + Team</span>
                    <span className="tool-sub">Traditional model</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ENTERPRISE_COMPARE_ROWS.map((row) => (
                  <tr key={row.capability}>
                    <th scope="row">{row.capability}</th>
                    <td className="ci">
                      <EnterpriseVsCell cell={row.induce} />
                    </td>
                    <td>
                      <EnterpriseVsCell cell={row.synthesia} />
                    </td>
                    <td>
                      <EnterpriseVsCell cell={row.runway} />
                    </td>
                    <td>
                      <EnterpriseVsCell cell={row.agency} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="vs-note">
            Based on publicly available product information as of May 2026. Competitor names are trademarks of their
            respective owners.
          </p>
        </div>
      </section>

      <section className="band-light tight">
        <div className="container">
          <div className="content-measure">
          <div className="sec-head reveal">
            <span className="label-mono">- Security &amp; compliance</span>
            <h2>
              Built for IT. <em>Cleared for legal.</em>
            </h2>
            <p className="sub">
              Everything on your procurement checklist - without a six-month security review.
            </p>
          </div>
          <div className="diffs reveal">
            {SECURITY_DIFFS.map((d) => (
              <div key={d.n} className="diff">
                <div className="n">
                  <span className="line" />
                  {d.n}
                </div>
                <h3>{d.title}</h3>
                <p>{d.body}</p>
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      <section className="spec band-dark" id="platform">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Platform specifications</span>
            <h2>
              Built for the <em>enterprise stack.</em>
            </h2>
            <p className="sub">The details your engineering lead needs before signing off.</p>
          </div>
          <div className="spec-grid reveal">
            <div className="spec-box">
              <div className="shead">
                <span className="lbl">{SPEC_WORKFLOW.title}</span>
                <span className="sbadge">{SPEC_WORKFLOW.badge}</span>
              </div>
              {SPEC_WORKFLOW.rows.map((row) => (
                <div key={row.key} className="spec-row">
                  <span className="skey">{row.key}</span>
                  <span className={`sval${'yes' in row && row.yes ? ' yes' : ''}`}>{row.value}</span>
                </div>
              ))}
            </div>
            <div className="spec-box">
              <div className="shead">
                <span className="lbl">{SPEC_ENTERPRISE.title}</span>
                <span className="sbadge">{SPEC_ENTERPRISE.badge}</span>
              </div>
              {SPEC_ENTERPRISE.rows.map((row) => (
                <div key={row.key} className="spec-row">
                  <span className="skey">{row.key}</span>
                  <span className={`sval${'yes' in row && row.yes ? ' yes' : ''}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PageFaqSection
        eyebrow="- Asked &amp; answered"
        title={
          <>
            Questions From
            <br />
            <em>Enterprise Teams.</em>
          </>
        }
        items={ENTERPRISE_FAQ}
      />

      <section className="ent-cta band-dark" id="cta">
        <div className="container reveal">
          <div className="ent-cta-copy">
            <div className="cta-overline">- Enterprise infrastructure</div>
            <h2>
              Build film production
              <br />
              into your <em>pipeline.</em>
            </h2>
            <p>
              Drop the AI video generator API into your content operations — brief ingestion through render, review,
              and delivery.
            </p>
            <div className="cta-acts">
              <TalkToFoundersButton variant="primary" />
              <SignUpButton variant="ghost" />
            </div>
            <div className="cta-note">
              SSO · SAML 2.0 · Volume pricing
              <br />
              Dedicated CSM · Pilot program available
            </div>
          </div>
          {/* <div className="ent-cta-card">
            <div className="ent-cta-card-head">
              Enterprise inquiry
              <span className="chd-badge">Pilot available</span>
            </div>
            <div className="ent-cta-card-body">
              <div className="ent-form-field">
                <label>Work email</label>
                <div className="ef-input">you@company.com</div>
              </div>
              <div className="ent-form-field">
                <label>Company</label>
                <div className="ef-input">Acme Corp</div>
              </div>
              <div className="ent-form-field">
                <label>Primary use case</label>
                <div className="ef-input">Product marketing videos</div>
              </div>
              <div className="ent-form-field">
                <label>Team size</label>
                <div className="ef-input">50 - 200 employees</div>
              </div>
            </div>
            <div className="ent-cta-card-foot">
              <TalkToFoundersButton className="!w-full !justify-center">
                Talk to Sales
              </TalkToFoundersButton>
            </div>
          </div> */}
        </div>
      </section>
    </div>
  );
}
